// pages/strategic-playbook/library.js
// ════════════════════════════════════════════════════
// STRATEGIC INTELLIGENCE — Library (v2 — unified)
// ════════════════════════════════════════════════════
// Single browse surface for all dossier types. Type
// filter chips switch which card layout renders:
//   - All / Situations  → standard dossier cards
//   - Thinkers          → portrait cards
//   - Shock Playbooks   → phase-strip cards
//   - Tectonic Shifts   → stage-progress cards
//
// URL-driven filter state (?type=thinker) so deep-links
// work and old /thinkers /shocks /shifts URLs can be
// redirected here.
// ════════════════════════════════════════════════════

import { useState, useMemo, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { DOSSIERS, PILLAR_NAMES } from '../../data/strategic-playbook/dossiers';
import { listTectonicShifts } from '../../data/strategic-playbook/tectonic-shifts';
import { listFindings } from '../../features/findings/registry';
import { listBriefs } from '../../data/lab/briefs';
import { listCollections } from '../../data/strategic-playbook/collections';

// Pull the latest stage-history year as a "recency" signal for shifts.
function latestShiftYear(shift) {
  const dates = (shift.stageHistory || []).map((h) => h.date || '');
  let latest = 0;
  for (const d of dates) {
    const m = d.match(/(\d{4})/g) || [];
    for (const yr of m) {
      const n = parseInt(yr, 10);
      if (n > latest) latest = n;
    }
  }
  return latest > 0 ? Date.UTC(latest, 11, 31) : 0;
}

// Adapter — maps a v3 tectonic shift entry into the card shape
// the library expects, so ShiftCard can render it without forks.
function tectonicShiftToCard(shift) {
  const firstDate = shift.stageHistory?.[0]?.date;
  const yearMatch = firstDate?.match(/(\d{4})/);
  const yearStarted = yearMatch ? parseInt(yearMatch[1], 10) : null;
  const namedCount = (shift.namedBeneficiaries?.length || 0) + (shift.namedTrapped?.length || 0);
  const watchMetricsCount = shift.watchMetrics?.length || 0;
  const lessonsCount = shift.lessons?.length || 0;
  const hasDepth = !!shift.contrarianRead || namedCount > 0 || watchMetricsCount > 0 || lessonsCount > 0;
  return {
    id: shift.slug,
    type: 'tectonic',
    eyebrow: shift.category === 'historical' ? 'Historical shift' : 'Tectonic shift',
    title: shift.name,
    shiftName: shift.name,
    summary: shift.thesis || shift.note,
    currentStage: shift.stageLabel,
    yearStarted,
    pillars: [],
    pillar: null,
    confidence: null,
    readingTimeMin: null,
    isV2Tectonic: true,
    slug: shift.slug,
    category: shift.category,
    era: shift.era,
    hasContrarian: !!shift.contrarianRead,
    namedCount,
    lessonsCount,
    watchMetricsCount,
    hasDepth,
    recentTs: latestShiftYear(shift),
  };
}

// Adapter — maps a Lab finding into the library card shape.
// Cards link to the bare-slug canonical URL (latest version).
function findingToCard(f) {
  const ts = f.preregisteredAt ? Date.parse(f.preregisteredAt) : 0;
  return {
    id: `finding/${f.domain}/${f.year}/${f.slug}`,
    type: 'finding',
    eyebrow: `Lab Finding · Form ${f.form} — ${f.formName}`,
    title: f.title,
    shortTitle: f.shortTitle,
    summary: f.description || '',
    pillars: [],
    pillar: null,
    confidence: null,
    readingTimeMin: null,
    isFinding: true,
    finding: f,
    href: f.path,
    citationHandle: f.citationHandle,
    lifecycle: f.lifecycle,
    visibility: f.visibility,
    domain: f.domain,
    year: f.year,
    form: f.form,
    formName: f.formName,
    sampleSize: f.sampleSize,
    preregisteredAt: f.preregisteredAt,
    hasDepth: true, // findings are authored = always deep
    recentTs: Number.isNaN(ts) ? 0 : ts,
  };
}

// Adapter — maps a weekly brief into the library card shape.
function briefToCard(b) {
  const ts = b.publishDate ? Date.parse(b.publishDate) : 0;
  return {
    id: `brief/${b.slug}`,
    type: 'brief',
    eyebrow: `Weekly Brief · ${b.weekOf || b.publishDate}`,
    title: b.title,
    summary: b.description || b.lede?.slice(0, 200) || '',
    pillars: [],
    pillar: null,
    confidence: null,
    readingTimeMin: null,
    isBrief: true,
    brief: b,
    href: `/lab/briefs/${b.slug}`,
    citationHandle: b.citationHandle,
    publishDate: b.publishDate,
    weekOf: b.weekOf,
    citedFindings: b.citedFindings || [],
    citedShifts: b.citedShifts || [],
    hasDepth: true, // briefs are authored = always deep
    recentTs: Number.isNaN(ts) ? 0 : ts,
  };
}

// Pre-built once: all tectonic shifts, findings, and briefs in the
// library's expected shape. Tectonic uses listTectonicShifts (merged
// with depth fields). Findings use listFindings (latest-version
// per slug). Briefs use listBriefs (most-recent first).
//
// Cross-relationship maps for relationship navigation:
//   FINDING_TO_SHIFT: maps a finding's citation handle → parent shift
//                     (the shift that lists this finding in its
//                     relatedFindings array).
//   SHIFT_TO_FINDINGS: maps a shift id → array of finding cards that
//                      this shift's relatedFindings cites.
const ALL_SHIFTS = listTectonicShifts();
const FINDING_TO_SHIFT = {};
for (const s of ALL_SHIFTS) {
  for (const handle of (s.relatedFindings || [])) {
    FINDING_TO_SHIFT[handle] = { id: s.id, slug: s.slug, name: s.name };
  }
}

const TECTONIC_V2_CARDS = ALL_SHIFTS.map((s) => {
  const card = tectonicShiftToCard(s);
  card.testedByCount = (s.relatedFindings || []).length;
  return card;
});
const FINDING_CARDS = listFindings().map((f) => {
  const card = findingToCard(f);
  card.parentShift = FINDING_TO_SHIFT[f.citationHandle] || null;
  return card;
});
const BRIEF_CARDS = listBriefs().map(briefToCard);

const TYPE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'finding', label: 'Lab Findings' },
  { id: 'brief', label: 'Weekly Briefs' },
  { id: 'tectonic', label: 'Tectonic Shifts' },
  { id: 'situation', label: 'Situations' },
  { id: 'thinker', label: 'Great Thinkers' },
  { id: 'shock-playbook', label: 'Shock Playbooks' },
];

const SORT_OPTIONS = [
  { id: 'default', label: 'Default Order' },
  { id: 'recency-desc', label: 'Most Recent' },
  { id: 'confidence-desc', label: 'Highest Confidence' },
  { id: 'confidence-asc', label: 'Lowest Confidence' },
  { id: 'reading-asc', label: 'Shortest Read' },
  { id: 'reading-desc', label: 'Longest Read' },
  { id: 'title-asc', label: 'A → Z' },
];

// Returns an ISO-style date string for recency sort. Newer = larger
// string, so descending sort puts most-recent first.
function getRecencySortKey(card) {
  if (card.preregisteredAt) return card.preregisteredAt;       // findings
  if (card.publishDate) return card.publishDate;                // briefs
  if (card.finding?.preregisteredAt) return card.finding.preregisteredAt;
  if (card.brief?.publishDate) return card.brief.publishDate;
  // For shifts: pull the most recent date out of stageHistory, else 0.
  // The library card doesn't carry stageHistory, but `yearStarted`
  // is a year integer we can use as a coarse fallback.
  if (card.yearStarted) return `${card.yearStarted}-01-01`;
  return '';
}

// Build the list of domains across the corpus once. Includes finding
// domains + a "(none)" bucket for entries without a domain.
const DOMAIN_LABELS = {
  popdec: 'Population Decline',
  strategic: 'Strategic Decisions',
  historical: 'Historical',
  trading: 'Trading',
};
const COLLECTIONS = listCollections();
const ALL_DOMAINS = (() => {
  const set = new Set();
  for (const f of FINDING_CARDS) if (f.domain) set.add(f.domain);
  return [...set];
})();

export default function StrategicPlaybookLibrary() {
  const router = useRouter();
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [pillarFilter, setPillarFilter] = useState([]);
  const [domainFilter, setDomainFilter] = useState([]);
  const [depthOnly, setDepthOnly] = useState(false);
  const [sortMode, setSortMode] = useState('default');

  // Sync filter state to URL query param so deep-links work
  useEffect(() => {
    if (!router.isReady) return;
    const queryType = router.query.type;
    if (typeof queryType === 'string') {
      const valid = TYPE_FILTERS.find((t) => t.id === queryType);
      if (valid) setTypeFilter(queryType);
    }
  }, [router.isReady, router.query.type]);

  function changeTypeFilter(newType) {
    setTypeFilter(newType);
    const url = newType === 'all'
      ? '/strategic-playbook/library'
      : `/strategic-playbook/library?type=${newType}`;
    router.replace(url, undefined, { shallow: true });
  }

  const filtered = useMemo(() => {
    // Library aggregates four content sources:
    //   - DOSSIERS minus legacy type='tectonic' (situations, thinkers, shocks)
    //   - TECTONIC_V2_CARDS (active + historical tectonic shifts)
    //   - FINDING_CARDS (Lab findings from registry)
    //   - BRIEF_CARDS (weekly briefs from briefs data)
    let list = DOSSIERS.filter((d) => d.type !== 'tectonic')
      .concat(TECTONIC_V2_CARDS)
      .concat(FINDING_CARDS)
      .concat(BRIEF_CARDS);

    if (typeFilter !== 'all') {
      list = list.filter((d) => d.type === typeFilter);
    }

    if (pillarFilter.length > 0) {
      list = list.filter((d) => d.pillars.some((p) => pillarFilter.includes(p)));
    }

    if (domainFilter.length > 0) {
      // Domain filter applies only to entries that carry a `domain` field
      // (currently findings). Entries without one pass through unchanged
      // when domain filter is empty; when set, only matching entries pass.
      list = list.filter((d) => !d.domain || domainFilter.includes(d.domain));
    }

    if (depthOnly) {
      // "Has depth" filter: hides tectonic shifts that haven't been
      // deepened (no contrarian read, no named positions, no lessons).
      // All other types pass through (they're authored by design).
      list = list.filter((d) => {
        if (d.type !== 'tectonic') return true;
        return d.hasContrarian || d.namedCount > 0 || d.lessonsCount > 0;
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((d) => {
        const haystack = [
          d.title, d.summary, d.eyebrow, d.thinkerName, d.coreIdea,
          d.shiftName, d.shockType, d.shortTitle, d.formName,
          d.citationHandle, d.weekOf,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(q);
      });
    }

    if (sortMode === 'recency-desc') {
      list.sort((a, b) => {
        const ka = getRecencySortKey(a);
        const kb = getRecencySortKey(b);
        return kb.localeCompare(ka);
      });
    } else if (sortMode === 'confidence-desc') {
      list.sort((a, b) => b.confidence - a.confidence);
    } else if (sortMode === 'confidence-asc') {
      list.sort((a, b) => a.confidence - b.confidence);
    } else if (sortMode === 'reading-asc') {
      list.sort((a, b) => a.readingTimeMin - b.readingTimeMin);
    } else if (sortMode === 'reading-desc') {
      list.sort((a, b) => b.readingTimeMin - a.readingTimeMin);
    } else if (sortMode === 'title-asc') {
      list.sort((a, b) => a.title.localeCompare(b.title));
    }

    return list;
  }, [typeFilter, search, pillarFilter, domainFilter, depthOnly, sortMode]);

  const togglePillar = (p) => {
    setPillarFilter((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  };

  const toggleDomain = (d) => {
    setDomainFilter((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
    );
  };

  const clearFilters = () => {
    changeTypeFilter('all');
    setSearch('');
    setPillarFilter([]);
    setDomainFilter([]);
    setDepthOnly(false);
    setSortMode('default');
  };

  const hasActiveFilters =
    typeFilter !== 'all' ||
    search.trim() !== '' ||
    pillarFilter.length > 0 ||
    domainFilter.length > 0 ||
    depthOnly ||
    sortMode !== 'default';

  const heroIntro = getHeroIntro(typeFilter);
  const gridMinWidth = getGridMinWidth(typeFilter);

  return (
    <div className="sp-root">
      <Head>
        <title>Library · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <div className="sp-topbar">
          <div>
            <h1>Strategic Intelligence</h1>
            <div className="sp-topbar-meta">
              Library · {DOSSIERS.filter((d) => d.type !== 'tectonic').length + TECTONIC_V2_CARDS.length + FINDING_CARDS.length + BRIEF_CARDS.length} entries
              {hasActiveFilters && (
                <>
                  {' '}· <span style={{ color: 'var(--sp-aged-brass)' }}>{filtered.length} match</span>
                </>
              )}
            </div>
          </div>
          <nav className="sp-topbar-nav">
            <Link href="/strategic-playbook">Dashboard</Link>
            <Link href="/strategic-playbook/library" className="sp-active">Library</Link>
            <Link href="/strategic-playbook/tools">Tools</Link>
            <Link href="/strategic-playbook/decisions">Decisions</Link>
            <Link href="/strategic-playbook/simulate">Simulate</Link>
          </nav>
        </div>

        {heroIntro && (
          <div style={{ marginBottom: 28, paddingBottom: 24, borderBottom: '1px solid var(--sp-divider)' }}>
            <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.20em', color: 'var(--sp-aged-brass)', marginBottom: 10 }}>
              {heroIntro.eyebrow}
            </div>
            <h2 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 24, lineHeight: 1.25, margin: '0 0 10px 0', color: 'var(--sp-warm-cream)', letterSpacing: '-0.005em' }}>
              {heroIntro.headline}
            </h2>
            <p style={{ fontSize: 13, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.78)', maxWidth: 720, margin: 0 }}>
              {heroIntro.body}
            </p>
          </div>
        )}

        {/* Curated paths — visible only on the unfiltered "all" view */}
        {typeFilter === 'all' && !search.trim() && COLLECTIONS.length > 0 && (
          <section className="library-collections-banner">
            <div className="library-collections-banner-head">
              <div className="library-collections-banner-eyebrow">§ Curated paths</div>
              <h2 className="library-collections-banner-title">
                Don&rsquo;t know where to start? Read in sequence.
              </h2>
              <p className="library-collections-banner-sub">
                Hand-picked reading orders through the corpus. Each carries
                editorial connective tissue between entries.
              </p>
            </div>
            <div className="library-collections-banner-grid">
              {COLLECTIONS.map((c) => (
                <Link
                  key={c.id}
                  href={`/strategic-playbook/collections/${c.slug}`}
                  className="library-collection-card"
                >
                  <div className="library-collection-card-eyebrow">{c.eyebrow}</div>
                  <h3 className="library-collection-card-name">{c.name}</h3>
                  <p className="library-collection-card-desc">{c.description}</p>
                  <div className="library-collection-card-arrow">Read path →</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div style={{ marginBottom: 18 }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search dossiers by title, situation, thinker..."
            style={{
              width: '100%',
              padding: '14px 18px',
              background: 'var(--sp-soft-charcoal)',
              border: `1px solid ${search ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
              borderRadius: 'var(--sp-radius-card)',
              color: 'var(--sp-warm-cream)',
              fontFamily: 'var(--sp-font-body)',
              fontSize: 14,
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(232, 221, 196, 0.45)', marginRight: 4 }}>
            Type
          </span>
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => changeTypeFilter(f.id)}
              style={{
                fontFamily: 'var(--sp-font-mono)', fontSize: 11,
                textTransform: 'uppercase', letterSpacing: '0.10em',
                padding: '8px 14px',
                background: typeFilter === f.id ? 'var(--sp-brass-bg)' : 'transparent',
                color: typeFilter === f.id ? 'var(--sp-aged-brass)' : 'rgba(232, 221, 196, 0.55)',
                border: `1px solid ${typeFilter === f.id ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
                borderRadius: 'var(--sp-radius)', cursor: 'pointer',
                transition: 'border-color 0.2s, color 0.2s, background 0.2s',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(232, 221, 196, 0.45)', marginRight: 4 }}>
            Pillars
          </span>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((p) => (
            <button
              key={p}
              onClick={() => togglePillar(p)}
              className={`sp-tag sp-pillar-bg-${p} sp-pillar-${p}`}
              style={{
                cursor: 'pointer',
                opacity: pillarFilter.length === 0 || pillarFilter.includes(p) ? 1 : 0.4,
                outline: pillarFilter.includes(p) ? '1px solid var(--sp-aged-brass)' : 'none',
                outlineOffset: 1,
                transition: 'opacity 0.2s', border: 'none',
                fontFamily: 'var(--sp-font-mono)', fontSize: 9,
                textTransform: 'uppercase', letterSpacing: '0.10em',
                padding: '4px 10px',
              }}
              title={PILLAR_NAMES[p]}
            >
              P{p}
            </button>
          ))}
          {pillarFilter.length > 0 && (
            <button
              onClick={() => setPillarFilter([])}
              style={{
                fontFamily: 'var(--sp-font-mono)', fontSize: 10,
                color: 'rgba(232, 221, 196, 0.55)', background: 'transparent',
                border: 'none', cursor: 'pointer', marginLeft: 4,
                textDecoration: 'underline', letterSpacing: '0.06em',
              }}
            >
              clear
            </button>
          )}
        </div>

        {/* Domain chips — only meaningful when browsing findings or all */}
        {(typeFilter === 'all' || typeFilter === 'finding') && ALL_DOMAINS.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(232, 221, 196, 0.45)', marginRight: 4 }}>
              Domain
            </span>
            {ALL_DOMAINS.map((d) => (
              <button
                key={d}
                onClick={() => toggleDomain(d)}
                style={{
                  cursor: 'pointer',
                  opacity: domainFilter.length === 0 || domainFilter.includes(d) ? 1 : 0.4,
                  background: domainFilter.includes(d) ? 'rgba(142, 90, 58, 0.18)' : 'transparent',
                  color: domainFilter.includes(d) ? 'var(--sp-cognac)' : 'rgba(232, 221, 196, 0.55)',
                  border: `1px solid ${domainFilter.includes(d) ? 'rgba(142, 90, 58, 0.45)' : 'var(--sp-divider)'}`,
                  borderRadius: 'var(--sp-radius)', padding: '4px 10px',
                  fontFamily: 'var(--sp-font-mono)', fontSize: 10,
                  textTransform: 'uppercase', letterSpacing: '0.10em',
                  transition: 'all 0.2s',
                }}
              >
                {DOMAIN_LABELS[d] || d}
              </button>
            ))}
            {domainFilter.length > 0 && (
              <button
                onClick={() => setDomainFilter([])}
                style={{
                  fontFamily: 'var(--sp-font-mono)', fontSize: 10,
                  color: 'rgba(232, 221, 196, 0.55)', background: 'transparent',
                  border: 'none', cursor: 'pointer', marginLeft: 4,
                  textDecoration: 'underline', letterSpacing: '0.06em',
                }}
              >
                clear
              </button>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.16em', color: 'rgba(232, 221, 196, 0.45)' }}>
            Sort
          </span>
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            style={{
              padding: '6px 10px', background: 'var(--sp-soft-charcoal)',
              border: '1px solid var(--sp-divider)', borderRadius: 'var(--sp-radius)',
              color: 'var(--sp-warm-cream)', fontFamily: 'var(--sp-font-body)',
              fontSize: 12, cursor: 'pointer',
            }}
          >
            {SORT_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>

          {/* Depth toggle */}
          <button
            onClick={() => setDepthOnly((v) => !v)}
            title="Hide tectonic shifts that haven't been deepened (no contrarian read, named positions, watch metrics, or lessons)"
            style={{
              fontFamily: 'var(--sp-font-mono)', fontSize: 10,
              padding: '7px 12px',
              background: depthOnly ? 'var(--sp-brass-bg)' : 'transparent',
              color: depthOnly ? 'var(--sp-aged-brass)' : 'rgba(232, 221, 196, 0.55)',
              border: `1px solid ${depthOnly ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
              borderRadius: 'var(--sp-radius)', cursor: 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.10em',
              transition: 'all 0.2s',
            }}
          >
            {depthOnly ? '✓ Depth only' : 'Depth filter'}
          </button>

          <div style={{ flex: 1 }} />

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                fontFamily: 'var(--sp-font-mono)', fontSize: 10,
                color: 'var(--sp-oxblood)', background: 'rgba(74, 31, 31, 0.10)',
                border: '1px solid rgba(74, 31, 31, 0.40)', borderRadius: 'var(--sp-radius)',
                padding: '6px 12px', cursor: 'pointer',
                textTransform: 'uppercase', letterSpacing: '0.10em',
              }}
            >
              Clear All
            </button>
          )}
        </div>

        {filtered.length === 0 ? (
          <EmptyState onClear={clearFilters} />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(${gridMinWidth}px, 1fr))`,
              gap: 18,
            }}
          >
            {filtered.map((d) => renderCard(d, search))}
          </div>
        )}

        <div className="sp-footer-mark">
          — {DOSSIERS.filter((d) => d.type !== 'tectonic').length + TECTONIC_V2_CARDS.length + FINDING_CARDS.length + BRIEF_CARDS.length} entries · 6 types · 8 pillars · indexed —
        </div>
      </div>
    </div>
  );
}

// ─── Type-aware card renderer ──────────────────────────
function renderCard(d, searchQuery) {
  if (d.type === 'finding') return <FindingCard key={d.id} card={d} />;
  if (d.type === 'brief') return <BriefCard key={d.id} card={d} />;
  if (d.type === 'thinker') return <ThinkerCard key={d.id} thinker={d} />;
  if (d.type === 'shock-playbook') return <ShockCard key={d.id} playbook={d} />;
  if (d.type === 'tectonic') return <ShiftCard key={d.id} shift={d} />;
  return <SituationCard key={d.id} dossier={d} searchQuery={searchQuery} />;
}

function SituationCard({ dossier, searchQuery }) {
  const renderHighlightedTitle = () => {
    if (!searchQuery || !searchQuery.trim()) return dossier.title;
    const q = searchQuery.toLowerCase();
    const titleLower = dossier.title.toLowerCase();
    const idx = titleLower.indexOf(q);
    if (idx === -1) return dossier.title;
    return (
      <>
        {dossier.title.slice(0, idx)}
        <mark style={{ background: 'rgba(156, 123, 63, 0.30)', color: 'var(--sp-aged-brass)', padding: '0 2px', borderRadius: 2 }}>
          {dossier.title.slice(idx, idx + searchQuery.length)}
        </mark>
        {dossier.title.slice(idx + searchQuery.length)}
      </>
    );
  };

  return (
    <Link href={`/strategic-playbook/${dossier.id}`} className="sp-dossier-card">
      <div className="sp-dossier-card-eyebrow">{dossier.eyebrow}</div>
      <h3 className="sp-dossier-card-title">{renderHighlightedTitle()}</h3>
      <p className="sp-dossier-card-summary">
        {dossier.summary.length > 200 ? dossier.summary.slice(0, 200) + '...' : dossier.summary}
      </p>
      <div className="sp-dossier-card-meta">
        {dossier.pillars.slice(0, 2).map((p) => (
          <span key={p} className={`sp-tag sp-pillar-bg-${p} sp-pillar-${p}`}>P{p}</span>
        ))}
        <span style={{ color: 'rgba(232, 221, 196, 0.40)', fontSize: 11 }}>{dossier.readingTimeMin} min</span>
        <span className="sp-dossier-card-confidence">{dossier.confidence}/100</span>
      </div>
    </Link>
  );
}

function ThinkerCard({ thinker }) {
  return (
    <Link href={`/strategic-playbook/${thinker.id}`} className="sp-dossier-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ height: 3, background: pillarToColor(thinker.primaryPillar), opacity: 0.7 }} />
      <div style={{ padding: '24px 26px' }}>
        <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
          Operating System
        </div>
        <div style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 26, lineHeight: 1.1, color: 'var(--sp-warm-cream)', margin: '0 0 6px 0', letterSpacing: '-0.015em' }}>
          {thinker.thinkerName}
        </div>
        <div style={{ fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 14, color: 'var(--sp-aged-brass)', margin: '0 0 14px 0', lineHeight: 1.4 }}>
          {thinker.coreIdea}
        </div>
        <div style={{ fontSize: 12, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.75)', margin: '0 0 16px 0' }}>
          {thinker.summary.length > 170 ? thinker.summary.slice(0, 170) + '...' : thinker.summary}
        </div>
        {thinker.signatureQuote && (
          <blockquote style={{ fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 12, lineHeight: 1.55, color: 'var(--sp-warm-cream)', borderLeft: '2px solid var(--sp-aged-brass)', paddingLeft: 12, margin: '0 0 14px 0' }}>
            &ldquo;{thinker.signatureQuote}&rdquo;
          </blockquote>
        )}
        <div style={{ paddingTop: 12, borderTop: '1px solid var(--sp-divider)', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          <Stat label="Horizon" value={thinker.timeHorizon} />
          <Stat label="Concentration" value={thinker.concentration} />
          <Stat label="Career" value={thinker.careerSpan} />
        </div>
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--sp-divider)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {thinker.pillars.slice(0, 2).map((p) => (
            <span key={p} className={`sp-tag sp-pillar-bg-${p} sp-pillar-${p}`}>P{p}</span>
          ))}
          <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'rgba(232, 221, 196, 0.45)' }}>
            {thinker.readingTimeMin} min · {thinker.confidence}/100
          </span>
          <div style={{ flex: 1 }} />
          <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'var(--sp-aged-brass)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Read →
          </span>
        </div>
      </div>
    </Link>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.10em', color: 'rgba(232, 221, 196, 0.40)', marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 11, color: 'var(--sp-warm-cream)', lineHeight: 1.3 }}>
        {value}
      </div>
    </div>
  );
}

function ShockCard({ playbook }) {
  const phases = ['Panic', 'Adapt', 'Cascade', 'Rebound', 'Permanence'];
  const phaseColors = ['var(--sp-oxblood)', 'var(--sp-cognac)', 'var(--sp-aged-brass)', 'var(--sp-forest-moss)', 'var(--sp-aged-bronze)'];

  return (
    <Link href={`/strategic-playbook/${playbook.id}`} className="sp-dossier-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ height: 3, background: 'var(--sp-oxblood)', opacity: 0.7 }} />
      <div style={{ padding: '22px 24px' }}>
        <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-oxblood)', marginBottom: 12 }}>
          Shock Playbook
        </div>
        <h3 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 18, lineHeight: 1.25, color: 'var(--sp-warm-cream)', margin: '0 0 10px 0', letterSpacing: '-0.005em' }}>
          {playbook.title}
        </h3>
        <p style={{ fontSize: 12, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.75)', margin: '0 0 16px 0' }}>
          {playbook.summary.length > 180 ? playbook.summary.slice(0, 180) + '...' : playbook.summary}
        </p>
        <div style={{ paddingTop: 12, borderTop: '1px solid var(--sp-divider)', marginBottom: 12 }}>
          <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(232, 221, 196, 0.40)', marginBottom: 8 }}>
            5-Phase Cascade
          </div>
          <div style={{ display: 'flex', gap: 3, marginBottom: 6 }}>
            {phases.map((ph, i) => (
              <div key={ph} style={{ flex: 1, height: 5, background: phaseColors[i], opacity: 0.7, borderRadius: 2 }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 3, fontFamily: 'var(--sp-font-mono)', fontSize: 8, color: 'rgba(232, 221, 196, 0.45)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {phases.map((ph) => (
              <div key={ph} style={{ flex: 1, textAlign: 'center' }}>{ph}</div>
            ))}
          </div>
        </div>
        <div style={{ paddingTop: 10, borderTop: '1px solid var(--sp-divider)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {playbook.pillars.slice(0, 2).map((p) => (
            <span key={p} className={`sp-tag sp-pillar-bg-${p} sp-pillar-${p}`}>P{p}</span>
          ))}
          <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'rgba(232, 221, 196, 0.45)' }}>
            {playbook.readingTimeMin} min · {playbook.confidence}/100
          </span>
          <div style={{ flex: 1 }} />
          <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'var(--sp-aged-brass)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Open →
          </span>
        </div>
      </div>
    </Link>
  );
}

function ShiftCard({ shift }) {
  const stages = ['Early', 'Accelerating', 'Peak', 'Declining'];
  // currentStage may be "Early" or "Early (in awareness)" — match by prefix
  const stageIndex = stages.findIndex((s) => (shift.currentStage || '').toLowerCase().startsWith(s.toLowerCase()));
  const safeStageIndex = stageIndex >= 0 ? stageIndex : 0;
  const yearsActive = 2026 - (shift.yearStarted || 2026);

  const accentColor =
    safeStageIndex === 0 ? 'var(--sp-forest-moss)' :
    safeStageIndex === 1 ? 'var(--sp-aged-brass)' :
    safeStageIndex === 2 ? 'var(--sp-cognac)' :
    'var(--sp-oxblood)';

  // v2 tectonic shifts route to the detail page; legacy entries
  // route to the long-form dossier reader.
  const href = shift.isV2Tectonic
    ? `/strategic-playbook/tectonic/${shift.slug}`
    : `/strategic-playbook/${shift.id}`;

  return (
    <Link href={href} className="sp-dossier-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ height: 3, background: accentColor, opacity: 0.75 }} />
      <div style={{ padding: '22px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)' }}>
            {shift.category === 'historical' ? 'Historical' : 'Tectonic'} Shift{shift.era ? ` · ${shift.era}` : (yearsActive > 0 ? ` · Year ${yearsActive}` : '')}
          </div>
          {shift.currentStage && (
            <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 9, padding: '3px 8px', border: '1px solid var(--sp-aged-brass)', borderRadius: 'var(--sp-radius)', color: 'var(--sp-aged-brass)', textTransform: 'uppercase', letterSpacing: '0.10em' }}>
              {shift.currentStage}
            </div>
          )}
        </div>
        <h3 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 22, lineHeight: 1.2, color: 'var(--sp-warm-cream)', margin: '0 0 14px 0', letterSpacing: '-0.005em' }}>
          {shift.shiftName || shift.title}
        </h3>
        {shift.currentStage && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(232, 221, 196, 0.40)', marginBottom: 6 }}>
              Stage Progress
            </div>
            <div style={{ display: 'flex', gap: 3, marginBottom: 6 }}>
              {stages.map((stage, i) => {
                let bg = 'rgba(232, 221, 196, 0.10)';
                if (i < safeStageIndex) bg = 'rgba(156, 123, 63, 0.45)';
                if (i === safeStageIndex) bg = 'var(--sp-aged-brass)';
                return <div key={stage} style={{ flex: 1, height: 5, background: bg, borderRadius: 2 }} />;
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--sp-font-mono)', fontSize: 8, color: 'rgba(232, 221, 196, 0.45)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {stages.map((stage) => (
                <div key={stage} style={{ flex: 1, textAlign: 'center' }}>{stage}</div>
              ))}
            </div>
          </div>
        )}
        <p style={{ fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 12, lineHeight: 1.55, color: 'rgba(232, 221, 196, 0.75)', margin: '0 0 12px 0' }}>
          {shift.summary.length > 140 ? shift.summary.slice(0, 140) + '...' : shift.summary}
        </p>
        {shift.testedByCount > 0 && (
          <div className="library-shift-card-tested">
            <span className="library-shift-card-tested-label">Tested by</span>{' '}
            <span className="library-shift-card-tested-count">
              {shift.testedByCount} Lab finding{shift.testedByCount === 1 ? '' : 's'}
            </span>
          </div>
        )}
        <div style={{ paddingTop: 10, borderTop: '1px solid var(--sp-divider)', display: 'flex', alignItems: 'center', gap: 8 }}>
          {shift.yearStarted && (
            <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'rgba(232, 221, 196, 0.45)' }}>
              Started {shift.yearStarted}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'var(--sp-aged-brass)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Open →
          </span>
        </div>
      </div>
    </Link>
  );
}

// ─── Finding card ─────────────────────────────────────
function FindingCard({ card }) {
  const lifecycleTone =
    card.lifecycle === 'published'      ? 'sage' :
    card.lifecycle === 'pre-registered' ? 'brass' :
    card.lifecycle === 'draft'          ? 'neutral' :
    card.lifecycle === 'revised'        ? 'cognac' :
    card.lifecycle === 'retracted'      ? 'oxblood' :
    'neutral';

  return (
    <Link href={card.href} className="library-finding-card">
      <div className="library-finding-card-eyebrow">
        Lab Finding · Form {card.form} — {card.formName}
      </div>
      <h3 className="library-finding-card-title">{card.title}</h3>
      {card.summary && (
        <p className="library-finding-card-desc">
          {card.summary.length > 200 ? card.summary.slice(0, 200) + '...' : card.summary}
        </p>
      )}
      <div className="library-finding-card-pills">
        <span className={`finding-version-badge finding-version-badge-${lifecycleTone}`}>
          {card.lifecycle || 'draft'}
        </span>
        {card.sampleSize > 0 && (
          <span className="library-finding-card-pill-n">N={card.sampleSize}</span>
        )}
        {card.domain && (
          <span className="library-finding-card-domain">{card.domain}</span>
        )}
      </div>
      {card.parentShift && (
        <div className="library-finding-card-parent">
          <span className="library-finding-card-parent-label">Tests mechanism of</span>{' '}
          <span className="library-finding-card-parent-name">{card.parentShift.name}</span>
        </div>
      )}
      <div className="library-finding-card-handle">
        <code>{card.citationHandle}</code>
      </div>
    </Link>
  );
}

// ─── Brief card ─────────────────────────────────────────
function BriefCard({ card }) {
  return (
    <Link href={card.href} className="library-brief-card">
      <div className="library-brief-card-meta">
        <span className="library-brief-card-week">{card.weekOf || card.publishDate}</span>
        <span className="library-brief-card-eyebrow">Weekly Brief</span>
      </div>
      <h3 className="library-brief-card-title">{card.title}</h3>
      {card.summary && (
        <p className="library-brief-card-desc">
          {card.summary.length > 200 ? card.summary.slice(0, 200) + '...' : card.summary}
        </p>
      )}
      <div className="library-brief-card-foot">
        {card.citedFindings?.length > 0 && (
          <span className="library-brief-card-foot-item">
            cites {card.citedFindings.length} finding{card.citedFindings.length === 1 ? '' : 's'}
          </span>
        )}
        {card.citedShifts?.length > 0 && (
          <span className="library-brief-card-foot-item">
            {card.citedShifts.length} shift{card.citedShifts.length === 1 ? '' : 's'}
          </span>
        )}
        <span className="library-brief-card-foot-arrow">Read →</span>
      </div>
    </Link>
  );
}

function EmptyState({ onClear }) {
  return (
    <div style={{ padding: 64, textAlign: 'center', color: 'rgba(232, 221, 196, 0.50)', fontFamily: 'var(--sp-font-serif)', fontStyle: 'italic', fontSize: 16 }}>
      No dossiers match your filters.
      <br />
      <button
        onClick={onClear}
        style={{
          marginTop: 16, fontFamily: 'var(--sp-font-mono)', fontSize: 11,
          color: 'var(--sp-aged-brass)', background: 'transparent',
          border: '1px solid var(--sp-aged-brass)', borderRadius: 'var(--sp-radius)',
          padding: '8px 14px', cursor: 'pointer',
          textTransform: 'uppercase', letterSpacing: '0.10em',
        }}
      >
        Clear filters
      </button>
    </div>
  );
}

function pillarToColor(pillarNum) {
  const map = {
    1: 'var(--sp-aged-brass)',
    2: 'var(--sp-forest-moss)',
    3: 'var(--sp-sage-olive)',
    4: 'var(--sp-deep-teal)',
    5: 'var(--sp-cognac)',
    6: 'var(--sp-oxblood)',
    7: 'var(--sp-plum)',
    8: 'var(--sp-aged-bronze)',
  };
  return map[pillarNum] || 'var(--sp-aged-brass)';
}

function getHeroIntro(typeFilter) {
  switch (typeFilter) {
    case 'finding':
      return {
        eyebrow: 'Lab Findings · Pre-registered, falsifiable, citable',
        headline: 'The load-bearing claims, structured so they can be wrong.',
        body: 'Each finding carries a thesis, evidence corpus, mechanism, honest exposure, and at least one pre-registered prediction with a public falsifier. Five narrative forms. Versioned, lifecycle-tracked, citable by handle.',
      };
    case 'brief':
      return {
        eyebrow: 'Weekly Briefs · Cross-corpus synthesis',
        headline: 'Not findings. The lighter weekly artifact.',
        body: 'Briefs synthesize what moved across tracked shifts + findings + predictions this week. They cite the load-bearing corpus underneath; they don\'t replace it.',
      };
    case 'thinker':
      return {
        eyebrow: 'Operating Systems · Not Stock Picks',
        headline: 'How elite thinkers actually think — and what their model costs.',
        body: 'Each profile is the operating system behind a public track record — what they look for, what they reject, the structural advantages most can\'t replicate, and the honest cost of running their playbook.',
      };
    case 'shock-playbook':
      return {
        eyebrow: 'Black Swan Playbook · Pre-built shock maps',
        headline: 'You can\'t predict the shock. You CAN pre-build the cascade.',
        body: 'Most cascades aren\'t predictable in the moment but ARE researchable ahead of the next instance. Each playbook maps the 5-phase cascade with proven beneficiaries by layer + the do-nothing baseline.',
      };
    case 'tectonic':
      return {
        eyebrow: 'Strategic Inflection Points · Long Arc',
        headline: 'You can\'t time the shift. You CAN position around it.',
        body: 'Each tracked shift has its own stage. The asymmetric move is positioning years 3-7 of a 10-year shift — not year 1, when picks-and-shovels are already priced.',
      };
    case 'situation':
      return {
        eyebrow: 'Situational Dossiers',
        headline: 'When you face this situation, here\'s what history says.',
        body: 'Specific situations users come to with — super-team dysfunction, asymmetric hires, comebacks from behind, defeat-to-refinement. 13-section dossiers indexed to user pain.',
      };
    default:
      return null;
  }
}

function getGridMinWidth(typeFilter) {
  if (typeFilter === 'shock-playbook') return 380;
  if (typeFilter === 'thinker') return 340;
  if (typeFilter === 'tectonic') return 340;
  if (typeFilter === 'finding') return 360;
  if (typeFilter === 'brief') return 380;
  return 320;
}
